import {
  Dropdown,
  type DropdownProps,
  isRedacted,
  type OptionType,
  type SingleValue,
} from '@omnipresentgroup/design-system';
import { useGetManagerEmployeesSimplified } from '@omnipresentgroup/domains';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { EMPLOYEE_ID_PARAM } from '../constants';

export const ManagerEmployeeSelector = ({
  companyId,
  label,
  size,
}: {
  companyId: string;
  label?: string;
  size?: DropdownProps<false>['size'];
}) => {
  const [searchParameters, setSearchParameters] = useSearchParams();

  const employeeId = new URLSearchParams(searchParameters).get(
    EMPLOYEE_ID_PARAM,
  ) as string;

  const { data: employeesData, isLoading: isLoadingEmployees } =
    useGetManagerEmployeesSimplified({
      companyId,
      enabled: !!companyId,
    });

  const employeeOptions: SingleValue<OptionType[]> = useMemo(
    () =>
      employeesData?.employees
        .map((employee) => ({
          id: employee.id,
          value: employee.id,
          label: employee.name,
          description: isRedacted(employee.email) ? 'Redacted' : employee.email,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || [],
    [employeesData],
  );

  return (
    <Dropdown
      isMulti={false}
      size={size}
      id="select-employee"
      label={label}
      placeholder="Search by employee name"
      onChange={(option, actionMeta) => {
        if (actionMeta.action === 'clear') {
          setSearchParameters((parameters) => {
            parameters.delete(EMPLOYEE_ID_PARAM);
            return parameters;
          });
        } else {
          setSearchParameters((parameters) => {
            parameters.set(
              EMPLOYEE_ID_PARAM,
              (option as SingleValue<OptionType>)?.value as string,
            );
            return parameters;
          });
        }
      }}
      isLoading={isLoadingEmployees}
      options={[
        {
          options: employeeOptions,
        },
      ]}
      isSearchable
      isClearable
      value={employeeOptions?.find((option) => option.value === employeeId)}
    />
  );
};
